import React from 'react';
import { Card } from 'primereact/card';

/**
 * 4-column highlight strip used on the agent profile hero.
 *
 * Same language as the v2.0 dashboard KPI cards (P15) but tuned for
 * smaller, signal-oriented stats — no breakdown bar, no week/month
 * comparison.
 *
 * Each item: { key, label, value, icon, color, unit?, sub? }
 *  - color is the surface accent (PrimeReact CSS-variable color name).
 *  - icon is a PrimeIcon class name without the `pi-` prefix? No — full
 *    name like `pi-users` — passed through unchanged.
 */
const HighlightStrip = ({ items }) => {
    if (!items?.length) return null;
    return (
        <div className="grid">
            {items.map((item) => {
                const clickable = typeof item.onClick === 'function';
                return (
                    <div key={item.key} className="col-6 lg:col">
                        <Card
                            className={`h-full shadow-1${clickable ? ' cursor-pointer hover:shadow-3 transition-shadow transition-duration-150' : ''}`}
                            style={{ borderLeft: `3px solid var(--${item.color}-500)` }}
                            pt={{ body: { className: 'p-2' }, content: { className: 'p-0' } }}
                            onClick={clickable ? item.onClick : undefined}
                            role={clickable ? 'button' : undefined}
                            tabIndex={clickable ? 0 : undefined}
                            onKeyDown={clickable ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    item.onClick();
                                }
                            } : undefined}
                        >
                            <div className="flex align-items-center justify-content-between mb-1">
                                <span className="text-600 text-xs font-medium">{item.label}</span>
                                <div
                                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                    style={{
                                        width: '1.75rem',
                                        height: '1.75rem',
                                        backgroundColor: `var(--${item.color}-100)`,
                                    }}
                                >
                                    <i
                                        className={`pi ${item.icon} text-xs`}
                                        style={{ color: `var(--${item.color}-600)` }}
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                            <div className="flex align-items-baseline gap-2">
                                <span
                                    className={`text-lg font-bold ${clickable ? 'text-primary' : 'text-900'}`}
                                >
                                    {item.value}
                                </span>
                                {item.unit && (
                                    <span className="text-500 text-xs">{item.unit}</span>
                                )}
                            </div>
                            {item.sub && (
                                <div className="text-500 text-xs mt-1">{item.sub}</div>
                            )}
                        </Card>
                    </div>
                );
            })}
        </div>
    );
};

export default HighlightStrip;
